<template>
    <DropdownMenu>
        <DropdownMenuTrigger>
            <div class="flex w-[330px] items-center gap-2 sm:w-full">
                <div
                    class="gradient-bg h-[36px] w-[36px] shrink-0 rounded-lg shadow-md"
                >
                    <!-- <img
                    class="h-auto rounded-md"
                    width="48"
                    height="48"
                    :src="user.profile_photo_url"
                    alt=""
                /> -->
                </div>
                <div class="w-full text-left">
                    <div class="flex w-full items-center justify-between">
                        <p class="w-full text-sm font-medium text-iron-700">
                            {{ user.name }}
                        </p>
                        <EllipsisVertical
                            :width="20"
                            :stroke-width="2"
                            class="-mr-2 text-iron-500"
                        />
                    </div>

                    <div class="w-full text-xs text-iron-500">
                        {{ user.email }}
                    </div>
                </div>
            </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
            class="w-56 -translate-x-10 -translate-y-3 px-3 sm:w-52 sm:translate-x-2 sm:px-0"
        >
            <template v-for="(navItem, i) of navItems" :key="i">
                <Link class="h-full w-full" :href="navItem.href">
                    <DropdownMenuItem
                        class="my-1.5 cursor-pointer gap-2.5 text-base sm:my-1.5 sm:text-sm"
                        v-if="
                            canShowNavItem(navItem) &&
                            navItem.separator === undefined
                        "
                    >
                        <component
                            :size="16"
                            :stroke-width="1.75"
                            class="text-iron-700"
                            v-if="navItem.icon"
                            :is="navItem.icon"
                        />

                        {{ navItem.title }}
                    </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator
                    v-if="canShowNavItem(navItem) && navItem.separator"
                />
            </template>

            <DropdownMenuItem
                class="cursor-pointer gap-2.5 text-base sm:hidden"
                @click="openSupportChatWindow"
            >
                <MessageCircleQuestion
                    :size="16"
                    :stroke-width="1.75"
                    class="text-iron-700"
                />

                Support
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
                @click="logout"
                class="cursor-pointer gap-2.5 text-base sm:text-sm"
            >
                <LogOut :size="16" :stroke-width="1.75" class="text-iron-700" />
                {{ $t("nav.log_out") }}
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>

<script lang="ts" setup>
import { usePage, Link, router } from "@inertiajs/vue3";

import { computed } from "vue";
import route from "ziggy-js";
import BookingsIcon from "./BookingsIcon.vue";
import EventsIcon from "./EventsIcon.vue";
import {
    ChevronsUpDown,
    User,
    Users,
    Globe,
    Puzzle,
    CreditCard,
    Palmtree,
    Lock,
    CircleOff,
    Coins,
    LogOut,
    Clock4,
    Waypoints,
    MessageCircleQuestion,
    EllipsisVertical,
} from "lucide-vue-next";
import { useI18n } from "vue-i18n";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const page = usePage();
const user = computed((): any => page.props.user);
const jetstream = computed((): any => page.props.jetstream);
const isAffiliatePartner = computed((): any => page.props.isAffiliatePartner);

const { t } = useI18n();

type NavItem = {
    title: string;
    separator?: boolean;
    icon?: any;
    href: string;
    onlyWhen?: string[];
    onlyMobile?: boolean;
};

function canShowNavItem(navItem: NavItem) {
    if (!navItem.onlyWhen) {
        return true;
    }

    return navItem.onlyWhen.some((onlyWhen) => {
        if (onlyWhen === "isAdmin") {
            return page.props.isAdmin;
        }

        if (onlyWhen === "isImpersonated") {
            return page.props.isImpersonated;
        }

        if (onlyWhen === "isAffil") {
            return isAffiliatePartner.value;
        }

        if (onlyWhen === "canCreateTeams") {
            return jetstream.value.canCreateTeams;
        }

        if (onlyWhen === "mobile") {
            return window.innerWidth < 640;
        }

        return false;
    });
}

const navItems: NavItem[] = [
    {
        title: t("nav.bookings"),
        href: route("app.dashboard"),
        onlyWhen: ["mobile"],
        icon: BookingsIcon,
    },
    {
        title: t("nav.event_types"),
        href: route("app.event-types.index"),
        onlyWhen: ["mobile"],
        icon: EventsIcon,
    },
    {
        href: route("app.availabilities.index"),
        title: t("nav.availability"),
        onlyWhen: ["mobile"],
        icon: Clock4,
    },
    {
        href: route("app.workflows.index"),
        title: t("nav.workflows"),
        onlyWhen: ["mobile"],
        icon: Waypoints,
    },
    {
        separator: true,
        title: "dumb",
        href: "",
        onlyWhen: ["mobile"],
    },
    {
        title: t("nav.profile"),
        href: route("profile.show"),
        icon: User,
    },

    {
        title: t("nav.integrations"),
        href: route("app.integrations.index"),
        icon: Puzzle,
    },
    {
        title: t("nav.billing"),
        href: route("app.billing"),
        icon: CreditCard,
    },
    {
        title: t("nav.affiliate"),
        href: route("app.affiliate-partners.show"),
        onlyWhen: ["isAffil"],
        icon: Coins,
    },
    {
        href: route("app.domains.index"),
        title: t("nav.domains"),
        icon: Globe,
    },
    {
        separator: true,
        title: "dumb",
        href: "",
    },
    {
        title: t("nav.teams"),
        href: route("app.teams.index"),
        onlyWhen: ["canCreateTeams"],
        icon: Users,
    },

    {
        title: "Kiosk",
        href: route("app.kiosk.users.index"),
        onlyWhen: ["isAdmin"],
        icon: Lock,
    },
    {
        title: "Back to my account",
        href: route("app.kiosk.users.leave-impersonation"),
        onlyWhen: ["isImpersonated"],
        icon: CircleOff,
    },
    {
        title: t("nav.days_off"),
        href: route("app.days_off.index"),
        icon: Palmtree,
    },
];

function logout() {
    router.post(
        route("logout"),
        {},
        {
            onFinish: () =>
                window.location.replace(
                    page.props.locale === "cs-CZ"
                        ? "https://zaptime.cz"
                        : "https://zaptime.app",
                ),
        },
    );
}

function openSupportChatWindow() {
    // @ts-expect-error
    window.$chatwoot.toggle("open"); // To open widget
}
</script>

<style scoped>
.gradient-bg {
    background: linear-gradient(
        137.17deg,
        #ff8d47 10.41%,
        #ff7447 22.14%,
        #ff4247 46.48%,
        #e92a5b 61%,
        #dd1c67 69.21%,
        #d31071 77.36%,
        #cf0283 89.83%
    );
}
</style>
